import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useEmployeeListRoster } from '../../../hooks/useEmployeeListRoster';
import {
  getZohoPeopleAuthUrl,
  disconnectZohoPeople,
  getZohoPeopleEmployees,
  autoMapZohoPeopleEmployees,
  saveZohoPeopleMappings,
  syncZohoPeopleTimeRange,
  exportAllZohoPeopleEmployees,
  previewZohoPeopleImport,
  importZohoPeopleEmployees,
  subscribeToZohoPeopleImportLogs,
  getZohoPeopleImportLogDetails,
  subscribeToZohoPeopleStatus,
} from '../../../services/zohoPeople';
import { ImportPreviewModal } from './ImportPreviewModal.component';
import {
  addNotification,
  notificationStates,
} from '../../../components/Notification';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import styles from './QuickBooksCard.module.scss';

const ZP_CONNECTED_PARAM = 'zp_connected';
const ZP_ERROR_PARAM = 'zp_error';

const ERROR_MESSAGES = {
  access_denied: 'Authorization was denied. Please try connecting again.',
  missing_params: 'Invalid OAuth response received.',
  invalid_state: 'OAuth state mismatch. Please try connecting again.',
  state_expired: 'Authorization timed out. Please try connecting again.',
  server_error: 'A server error occurred. Please try again.',
};

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function fmtDate(dateObj) {
  if (!dateObj) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(dateObj);
}

export const ZohoPeopleCard = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );
  const location = useLocation();

  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const isAdmin = userProfile?.activeCompany?.role === 'Admin';
  const teamId = activeTeamId;

  const { employees: prodaffEmployees, isLoading: rosterLoading } =
    useEmployeeListRoster(teamId);

  const [zpStatus, setZpStatus] = useState({
    connected: false,
    connectedAt: null,
    connectedBy: null,
    employeeMappings: {},
    employeeMappingsUpdatedAt: null,
    lastSyncAt: null,
    lastSyncStats: null,
  });

  const [zohoEmployees, setZohoEmployees] = useState([]);
  const [zohoEmployeesLoading, setZohoEmployeesLoading] = useState(false);
  const [pendingMappings, setPendingMappings] = useState({});

  const [syncRange, setSyncRange] = useState({
    start: firstOfMonthStr(),
    end: todayStr(),
  });
  const [importLogs, setImportLogs] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsTitle, setDetailsTitle] = useState('');

  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);

  // ── Handle OAuth callback URL params ────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get(ZP_CONNECTED_PARAM);
    const errorCode = params.get(ZP_ERROR_PARAM);
    if (!connected && !errorCode) return;

    if (connected === 'true') {
      addNotification({
        message: 'Zoho People connected successfully.',
        level: notificationStates.success,
      });
    } else if (errorCode) {
      addNotification({
        message:
          ERROR_MESSAGES[errorCode] ||
          'Failed to connect Zoho People. Please try again.',
        level: notificationStates.error,
      });
    }

    // Clean URL params without reloading
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
  }, [location.search]);

  // ── Firestore real-time status + import logs ────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const unsub = subscribeToZohoPeopleStatus(companyId, setZpStatus);
    return unsub;
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !zpStatus.connected) return;
    const unsub = subscribeToZohoPeopleImportLogs(companyId, setImportLogs);
    return unsub;
  }, [companyId, zpStatus.connected]);

  // ── Load Zoho People employees when connected ───────────────────────────────
  useEffect(() => {
    if (!zpStatus.connected || !companyId) {
      setZohoEmployees([]);
      return;
    }
    setZohoEmployeesLoading(true);
    getZohoPeopleEmployees(companyId)
      .then(({ employees }) => setZohoEmployees(employees || []))
      .catch(() =>
        addNotification({
          message: 'Failed to load Zoho People employees.',
          level: notificationStates.error,
        })
      )
      .finally(() => setZohoEmployeesLoading(false));
  }, [zpStatus.connected, companyId]);

  const effectiveMappings = useMemo(() => {
    const base = zpStatus.employeeMappings || {};
    const merged = { ...base };
    for (const [uid, val] of Object.entries(pendingMappings)) {
      if (val === null) delete merged[uid];
      else merged[uid] = val;
    }
    return merged;
  }, [zpStatus.employeeMappings, pendingMappings]);

  const hasPendingChanges = Object.keys(pendingMappings).length > 0;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleConnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can connect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setConnecting(true);
    try {
      const { authUrl } = await getZohoPeopleAuthUrl(companyId, userId);
      window.location.href = authUrl;
    } catch {
      setConnecting(false);
      addNotification({
        message:
          'Failed to initiate Zoho People authorization. Please try again.',
        level: notificationStates.error,
      });
    }
  }, [companyId, userId, isAdmin]);

  const handleDisconnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can disconnect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setDisconnecting(true);
    try {
      await disconnectZohoPeople(companyId, userId);
      setPendingMappings({});
      addNotification({
        message: 'Zoho People disconnected.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to disconnect Zoho People. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setDisconnecting(false);
    }
  }, [companyId, userId, isAdmin]);

  const handleAutoMap = useCallback(async () => {
    setAutoMapping(true);
    try {
      const { mapped, unmatched } = await autoMapZohoPeopleEmployees(
        companyId,
        teamId
      );
      setPendingMappings({});
      const total = mapped + (unmatched?.length ?? 0);
      addNotification({
        message: `Auto-mapped ${mapped} of ${total} employees by email.`,
        level:
          mapped > 0 ? notificationStates.success : notificationStates.warning,
      });
    } catch {
      addNotification({
        message: 'Auto-mapping failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setAutoMapping(false);
    }
  }, [companyId, teamId]);

  const handleSelectMapping = useCallback(
    (prodaffUserId, zohoId) => {
      if (!zohoId) {
        setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
        return;
      }
      const emp = zohoEmployees.find((e) => String(e.id) === String(zohoId));
      if (!emp) return;
      setPendingMappings((prev) => ({
        ...prev,
        [prodaffUserId]: {
          zohoEmployeeId: emp.id,
          zohoEmployeeName: emp.name,
          zohoEmployeeRef: emp.employeeId,
        },
      }));
    },
    [zohoEmployees]
  );

  const handleUnmap = useCallback((prodaffUserId) => {
    setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
  }, []);

  const handleSaveMappings = useCallback(async () => {
    if (!hasPendingChanges) return;
    setSavingMappings(true);
    try {
      await saveZohoPeopleMappings(companyId, pendingMappings);
      setPendingMappings({});
      addNotification({
        message: 'Employee mappings saved.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to save mappings. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSavingMappings(false);
    }
  }, [companyId, pendingMappings, hasPendingChanges]);

  const handleExportAll = useCallback(async () => {
    setExportingAll(true);
    try {
      const { mapped, created, skipped, errors } =
        await exportAllZohoPeopleEmployees(companyId, teamId);
      setPendingMappings({});
      const parts = [];
      if (created > 0) parts.push(`${created} created`);
      if (mapped > 0) parts.push(`${mapped} matched by email`);
      if (skipped > 0) parts.push(`${skipped} already mapped`);
      if (errors?.length > 0) parts.push(`${errors.length} failed`);
      addNotification({
        message: `Export complete: ${parts.join(', ') || 'nothing to do'}.`,
        level:
          errors?.length > 0
            ? notificationStates.warning
            : notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to export employees. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setExportingAll(false);
    }
  }, [companyId, teamId]);

  const handleOpenImportPreview = useCallback(async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setImportResults(null);
    try {
      const data = await previewZohoPeopleImport(companyId, teamId, userId);
      setPreviewData(data);
    } catch {
      addNotification({
        message: 'Failed to load import preview. Please try again.',
        level: notificationStates.error,
      });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [companyId, teamId, userId]);

  const handleClosePreviewModal = useCallback(() => {
    if (importing) return;
    setPreviewOpen(false);
    setPreviewData(null);
    setImportResults(null);
  }, [importing]);

  const handleConfirmImport = useCallback(async () => {
    setImporting(true);
    try {
      const result = await importZohoPeopleEmployees(companyId, teamId, userId);
      setImportResults(result);
    } catch {
      addNotification({
        message:
          'Failed to import employees from Zoho People. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setImporting(false);
    }
  }, [companyId, teamId, userId]);

  const handleCloseDetailsModal = useCallback(() => {
    setDetailsOpen(false);
    setDetailsData(null);
  }, []);

  const handleViewDetails = useCallback(
    async (log) => {
      setDetailsLoading(true);
      setDetailsOpen(true);
      setDetailsData(null);
      setDetailsTitle(`Zoho People Import · ${fmtDate(log.importedAt)}`);
      try {
        const detail = await getZohoPeopleImportLogDetails(companyId, log.id);
        setDetailsData({
          ...log.stats,
          employeeResults: detail.employees || [],
        });
      } catch {
        addNotification({
          message: 'Failed to load import details.',
          level: notificationStates.error,
        });
        setDetailsOpen(false);
      } finally {
        setDetailsLoading(false);
      }
    },
    [companyId]
  );

  const handleSync = useCallback(async () => {
    if (!syncRange.start || !syncRange.end) {
      addNotification({
        message: 'Please select a valid date range.',
        level: notificationStates.error,
      });
      return;
    }
    setSyncing(true);
    try {
      const result = await syncZohoPeopleTimeRange(
        companyId,
        teamId,
        syncRange.start,
        syncRange.end
      );
      addNotification({
        message: `Sync complete. ${result.synced ?? 0} records synced.`,
        level: notificationStates.success,
      });
    } catch (err) {
      addNotification({
        message:
          err?.response?.data?.message || 'Sync failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSyncing(false);
    }
  }, [companyId, teamId, syncRange]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <ImportPreviewModal
        isOpen={previewOpen}
        onClose={handleClosePreviewModal}
        onConfirm={handleConfirmImport}
        preview={previewData}
        loading={previewLoading}
        confirming={importing}
        source="Zoho People"
        results={importResults}
      />
      <ImportPreviewModal
        isOpen={detailsOpen}
        onClose={handleCloseDetailsModal}
        preview={null}
        loading={detailsLoading}
        confirming={false}
        source="Zoho People"
        results={detailsData}
        resultsTitle={detailsTitle}
      />
      {/* Connection status panel */}
      <div className={styles.statusPanel}>
        <div className={styles.statusLeft}>
          <span
            className={`${styles.badge} ${zpStatus.connected ? styles.connected : styles.disconnected}`}
          >
            {zpStatus.connected ? 'Connected' : 'Not Connected'}
          </span>
          {zpStatus.connected ? (
            <span className={styles.connectedMeta}>
              {zpStatus.connectedAt &&
                `Connected ${fmtDate(zpStatus.connectedAt)}`}
            </span>
          ) : (
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Connect your Zoho People account to start syncing attendance data.
            </Text>
          )}
        </div>
        <div className={styles.statusRight}>
          {isAdmin && !zpStatus.connected && (
            <Button
              text={connecting ? 'Redirecting…' : 'Connect with Zoho People'}
              variant={buttonVariants.primary}
              size={buttonSizes.sm}
              onClick={handleConnect}
              disabled={connecting}
            />
          )}
          {isAdmin && zpStatus.connected && (
            <Button
              text={disconnecting ? 'Disconnecting…' : 'Disconnect'}
              variant={buttonVariants.secondary}
              size={buttonSizes.sm}
              onClick={handleDisconnect}
              disabled={disconnecting}
            />
          )}
        </div>
      </div>

      {/* Employee Mapping panel */}
      {zpStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Employee Mapping</span>
              <span className={styles.sectionSubtitle}>
                Map Prodaff employees to their Zoho People employee records.
              </span>
            </div>
            <div className={styles.sectionActions}>
              <Button
                text={autoMapping ? 'Mapping…' : 'Auto-map by Email'}
                variant={buttonVariants.secondary}
                size={buttonSizes.sm}
                onClick={handleAutoMap}
                disabled={
                  autoMapping ||
                  exportingAll ||
                  importing ||
                  zohoEmployeesLoading
                }
              />
              <Button
                text={exportingAll ? 'Exporting…' : 'Export All to Zoho People'}
                variant={buttonVariants.secondary}
                size={buttonSizes.sm}
                onClick={handleExportAll}
                disabled={
                  exportingAll ||
                  autoMapping ||
                  importing ||
                  zohoEmployeesLoading
                }
              />
              <Button
                text={importing ? 'Importing…' : 'Import from Zoho People'}
                variant={buttonVariants.primary}
                size={buttonSizes.sm}
                onClick={handleOpenImportPreview}
                disabled={
                  importing ||
                  autoMapping ||
                  exportingAll ||
                  zohoEmployeesLoading
                }
              />
            </div>
          </div>

          <div className={styles.sectionBody}>
            {rosterLoading || zohoEmployeesLoading ? (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                color="gray-500"
              >
                Loading…
              </Text>
            ) : prodaffEmployees.length === 0 ? (
              <EmptyState
                illustration
                title="No Employees Found"
                description="No employees found in this department to map to Zoho People."
              />
            ) : (
              <>
                <table className={styles.mappingTable}>
                  <thead>
                    <tr>
                      <th>Prodaff Employee</th>
                      <th>Zoho People Employee</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prodaffEmployees.map((emp) => {
                      const mapping = effectiveMappings[emp.userId];
                      return (
                        <tr key={emp.userId}>
                          <td>
                            <div className={styles.employeeName}>
                              {emp.name}
                            </div>
                            <div className={styles.employeeEmail}>
                              {emp.email}
                            </div>
                          </td>
                          <td>
                            {mapping ? (
                              <div className={styles.mappingCell}>
                                <span className={styles.mappedTag}>
                                  {mapping.zohoEmployeeName ||
                                    mapping.zohoEmployeeId}
                                  {mapping.zohoEmployeeRef && (
                                    <span
                                      style={{
                                        color: '#60a5fa',
                                        fontWeight: 400,
                                      }}
                                    >
                                      {' '}
                                      #{mapping.zohoEmployeeRef}
                                    </span>
                                  )}
                                  <button
                                    className={styles.unmapBtn}
                                    onClick={() => handleUnmap(emp.userId)}
                                    title="Remove mapping"
                                  >
                                    ×
                                  </button>
                                </span>
                              </div>
                            ) : (
                              <select
                                className={styles.qbSelect}
                                value=""
                                onChange={(e) =>
                                  handleSelectMapping(
                                    emp.userId,
                                    e.target.value
                                  )
                                }
                                disabled={zohoEmployees.length === 0}
                              >
                                <option value="">
                                  — Select Zoho People employee —
                                </option>
                                {zohoEmployees.map((e) => (
                                  <option key={e.id} value={e.id}>
                                    {e.name}
                                    {e.employeeId !== e.id
                                      ? ` (#${e.employeeId})`
                                      : ''}
                                    {e.email ? ` · ${e.email}` : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            <div className={styles.actionCell} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className={styles.saveMappingsRow}>
                  {hasPendingChanges && (
                    <span className={styles.pendingNote}>Unsaved changes</span>
                  )}
                  <Button
                    text={savingMappings ? 'Saving…' : 'Save Mappings'}
                    variant={buttonVariants.primary}
                    size={buttonSizes.sm}
                    onClick={handleSaveMappings}
                    disabled={savingMappings || !hasPendingChanges}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Import History panel */}
      {zpStatus.connected && importLogs.length > 0 && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Import History</span>
              <span className={styles.sectionSubtitle}>
                Last {importLogs.length} import run
                {importLogs.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            {importLogs.map((log) => (
              <div key={log.id} className={styles.historyRow}>
                <div className={styles.historyInfo}>
                  <span className={styles.historyDate}>
                    {fmtDate(log.importedAt)}
                  </span>
                  <span className={styles.historyMeta}>
                    by{' '}
                    {log.importedBy?.name ||
                      log.importedBy?.userId ||
                      'Unknown'}
                  </span>
                </div>
                <div className={styles.historyStats}>
                  {(log.stats?.imported || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipGreen}`}>
                      {log.stats.imported} created
                    </span>
                  )}
                  {(log.stats?.invited || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipBlue}`}>
                      {log.stats.invited} invited
                    </span>
                  )}
                  {(log.stats?.mapped || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipGray}`}>
                      {log.stats.mapped} linked
                    </span>
                  )}
                  {(log.stats?.errors || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipRed}`}>
                      {log.stats.errors} failed
                    </span>
                  )}
                  {(log.stats?.imported || 0) === 0 &&
                    (log.stats?.invited || 0) === 0 &&
                    (log.stats?.mapped || 0) === 0 &&
                    (log.stats?.errors || 0) === 0 && (
                      <span className={`${styles.chip} ${styles.chipGray}`}>
                        nothing to import
                      </span>
                    )}
                </div>
                <Button
                  text="Details"
                  variant={buttonVariants.secondary}
                  size={buttonSizes.sm}
                  onClick={() => handleViewDetails(log)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync panel */}
      {zpStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Sync Attendance</span>
              <span className={styles.sectionSubtitle}>
                Push Prodaff tracked hours to Zoho People as attendance records
                for a selected date range.
              </span>
            </div>
          </div>

          <div className={styles.sectionBody}>
            <div className={styles.syncRow}>
              <div className={styles.dateField}>
                <label htmlFor="zp-start-date">From</label>
                <input
                  id="zp-start-date"
                  type="date"
                  value={syncRange.start}
                  max={syncRange.end || todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, start: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <div className={styles.dateField}>
                <label htmlFor="zp-end-date">To</label>
                <input
                  id="zp-end-date"
                  type="date"
                  value={syncRange.end}
                  min={syncRange.start}
                  max={todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, end: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <Button
                text={syncing ? 'Syncing…' : 'Sync Now'}
                variant={buttonVariants.primary}
                size={buttonSizes.sm}
                onClick={handleSync}
                disabled={syncing || !syncRange.start || !syncRange.end}
              />
            </div>

            {zpStatus.lastSyncAt && (
              <div className={styles.syncStats}>
                <div className={styles.syncStatsTitle}>
                  Last sync · {fmtDate(zpStatus.lastSyncAt)}
                  {zpStatus.lastSyncStats?.startDate &&
                    zpStatus.lastSyncStats?.endDate && (
                      <span style={{ fontWeight: 400, color: '#64748b' }}>
                        {' '}
                        ({zpStatus.lastSyncStats.startDate} →{' '}
                        {zpStatus.lastSyncStats.endDate})
                      </span>
                    )}
                </div>
                {zpStatus.lastSyncStats && (
                  <div className={styles.syncStatsGrid}>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Synced</span>
                      <span className={`${styles.statValue} ${styles.success}`}>
                        {zpStatus.lastSyncStats.synced ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Skipped</span>
                      <span className={`${styles.statValue} ${styles.warning}`}>
                        {zpStatus.lastSyncStats.skipped ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Errors</span>
                      <span className={`${styles.statValue} ${styles.error}`}>
                        {zpStatus.lastSyncStats.errors ?? 0}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
